import { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Collapse,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import classnames from "classnames";
import dayjs from "dayjs";
import toastr from "toastr";
import WorkerMiniProfile from "../../../components/WorkerMiniProfile/WorkerMiniProfile";
import {
  useAttendanceNoShowDecision,
  useGetClientBookingAttendance,
} from "../../../api/booking";
// import BookingAttendanceScanner from "./BookingAttendanceScanner";
import { useNoShowAttendanceWindow } from "./useNoShowAttendanceWindow";
import {
  ATTENDANCE_STATUS_FILTERS,
  attendanceFilterKey,
  attendanceResponseMatchesRequest,
  extractAttendanceRows,
  formatAttendanceDateTime,
  formatAttendanceTotalTime,
  getShiftDates,
  normalizeShiftDate,
} from "./attendanceHelpers";

function ShiftAttendancePanel({
  bookingSlug,
  bookingTimezone,
  shift,
  shiftKey,
  isOpen,
  activeDateByShift,
  setActiveDateByShift,
  statusFilterByKey,
  setStatusFilterByKey,
  onOpenProfile,
}) {
  const dates = getShiftDates(shift);
  const activeDate = useMemo(() => {
    if (!dates.length) return null;
    const stored = activeDateByShift[shiftKey];
    return normalizeShiftDate(stored) ?? dates[0];
  }, [activeDateByShift, dates, shiftKey]);

  const statusFilter = useMemo(() => {
    if (!activeDate) return "all";
    const key = attendanceFilterKey(shiftKey, activeDate);
    return statusFilterByKey[key] ?? "all";
  }, [activeDate, shiftKey, statusFilterByKey]);

  const {
    data: attendanceRes,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useGetClientBookingAttendance({
    bookingSlug,
    shiftIndex: shiftKey,
    date: activeDate,
    filter: statusFilter,
    page: 1,
    limit: 100,
    enabled: isOpen && !!bookingSlug && !!activeDate,
  });

  const requestParams = useMemo(
    () => ({
      bookingSlug,
      shiftIndex: shiftKey,
      date: activeDate,
      filter: statusFilter,
    }),
    [bookingSlug, shiftKey, activeDate, statusFilter]
  );

  const responseReady = attendanceResponseMatchesRequest(
    attendanceRes,
    requestParams
  );

  const filteredRows = useMemo(() => {
    if (!responseReady) return [];
    return extractAttendanceRows(attendanceRes);
  }, [attendanceRes, responseReady]);

  const noShowDecision = useAttendanceNoShowDecision();

  const { meta, bannerState } = useNoShowAttendanceWindow({
    enabled: isOpen && responseReady,
    activeDate,
    bookingTimezone,
    attendanceRes,
    responseReady,
    refetch,
  });

  const handleNoShowAction = (mode) => {
    if (!bookingSlug || activeDate == null) return;
    noShowDecision.mutate(
      {
        bookingSlug,
        shiftIndex: shiftKey,
        date: activeDate,
        mode,
      },
      {
        onSuccess: () => {
          toastr.success(
            mode === "waiting"
              ? "Waiting for remaining workers."
              : "Replacement matching started."
          );
          refetch();
        },
      }
    );
  };

  const showLoading = isLoading || isFetching || !responseReady;

  if (!isOpen) return null;

  return (
    <CardBody className="pt-0">
      {dates.length === 0 ? (
        <div className="text-muted py-3">No dates scheduled for this shift.</div>
      ) : (
        <>
          <div className="client-booking-attendance-date-tabs mb-3">
            <Nav pills className="nav-pills-custom flex-nowrap mb-0">
              {dates.map((date) => (
                <NavItem key={date} className="flex-shrink-0">
                  <NavLink
                    className={classnames({ active: activeDate === date })}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveDateByShift((prev) => ({
                        ...prev,
                        [shiftKey]: date,
                      }));
                    }}
                  >
                    {dayjs(date).format("DD MMM YYYY")}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          </div>

          {bannerState.showBanner && meta ? (
            <Alert color="warning" className="mb-3">
              <p className="mb-2">
                Some workers did not show for this shift. What would you like to
                do?
              </p>
              <small className="text-muted d-block mb-3">
                {meta.totalWorkerCheckInCount} of {meta.totalWorkerCount} workers
                checked in
              </small>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  color="secondary"
                  outline
                  disabled={noShowDecision.isPending}
                  onClick={() => handleNoShowAction("waiting")}
                >
                  {noShowDecision.isPending ? (
                    <Spinner size="sm" />
                  ) : (
                    "Wait"
                  )}
                </Button>
                <Button
                  color="primary"
                  disabled={noShowDecision.isPending}
                  onClick={() => handleNoShowAction("startSmartMatching")}
                >
                  {noShowDecision.isPending ? (
                    <Spinner size="sm" />
                  ) : (
                    "Find Replacement"
                  )}
                </Button>
              </div>
            </Alert>
          ) : null}

          <Row className="mb-3 align-items-end">
            <Col md={4} sm={6}>
              <Label
                for={`attendance-status-${shiftKey}`}
                className="form-label mb-1 text-muted small"
              >
                Attendance status
              </Label>
              <Input
                id={`attendance-status-${shiftKey}`}
                type="select"
                value={statusFilter}
                onChange={(e) => {
                  if (!activeDate) return;
                  const key = attendanceFilterKey(shiftKey, activeDate);
                  setStatusFilterByKey((prev) => ({
                    ...prev,
                    [key]: e.target.value,
                  }));
                }}
              >
                {ATTENDANCE_STATUS_FILTERS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={8} sm={6} className="text-md-end">
              <div className="d-flex align-items-center justify-content-md-end gap-2">
                {isFetching ? <Spinner size="sm" color="primary" /> : null}
                <small className="text-muted">
                  {responseReady
                    ? `${filteredRows.length} worker${filteredRows.length !== 1 ? "s" : ""}`
                    : "Loading…"}
                </small>
              </div>
            </Col>
          </Row>

          {showLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <p className="text-muted mt-2 mb-0">Loading attendance…</p>
            </div>
          ) : isError ? (
            <div className="text-center py-4">
              <p className="text-danger mb-2">Failed to load attendance.</p>
              <Button color="primary" size="sm" outline onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="text-muted text-center py-4">
              No attendance records for this date.
            </div>
          ) : (
            <div className="table-responsive">
              <Table
                className="table-sm align-middle mb-0 border-top"
                responsive
              >
                <thead className="table-light">
                  <tr>
                    <th>Worker name</th>
                    <th>Phone</th>
                    <th>Check in</th>
                    <th>Check out</th>
                    <th>Total time</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <Button
                          color="link"
                          className="p-0 fw-semibold text-start text-decoration-none"
                          onClick={() => onOpenProfile(row)}
                        >
                          {row.fullName}
                        </Button>
                      </td>
                      <td>{row.phoneNumber || "—"}</td>
                      <td>{formatAttendanceDateTime(row.checkInTime)}</td>
                      <td>{formatAttendanceDateTime(row.checkOutTime)}</td>
                      <td>{formatAttendanceTotalTime(row)}</td>
                      <td className="text-end">
                        <Button
                          color="primary"
                          outline
                          size="sm"
                          onClick={() => onOpenProfile(row)}
                        >
                          View profile
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </>
      )}
    </CardBody>
  );
}

ShiftAttendancePanel.propTypes = {
  bookingSlug: PropTypes.string.isRequired,
  bookingTimezone: PropTypes.string,
  shift: PropTypes.object.isRequired,
  shiftKey: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  activeDateByShift: PropTypes.object.isRequired,
  setActiveDateByShift: PropTypes.func.isRequired,
  statusFilterByKey: PropTypes.object.isRequired,
  setStatusFilterByKey: PropTypes.func.isRequired,
  onOpenProfile: PropTypes.func.isRequired,
};

export default function BookingAttendanceTab({ booking }) {
  const bookingSlug = booking?.slug;

  const shiftPatterns = useMemo(
    () => (Array.isArray(booking?.shiftPattern) ? booking.shiftPattern : []),
    [booking?.shiftPattern]
  );

  const [openShiftIndex, setOpenShiftIndex] = useState(0);
  const [activeDateByShift, setActiveDateByShift] = useState({});
  const [statusFilterByKey, setStatusFilterByKey] = useState({});
  const [profileUser, setProfileUser] = useState(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const getShiftKey = useCallback((shift, arrayIndex) => {
    return typeof shift?.shiftIndex === "number" ? shift.shiftIndex : arrayIndex;
  }, []);

  const openProfileModal = useCallback((row) => {
    if (!row?.profileUser) return;
    setProfileUser(row.profileUser);
    setIsProfileOpen(true);
  }, []);

  const toggleShift = (shiftIndex) => {
    setOpenShiftIndex((prev) => (prev === shiftIndex ? -1 : shiftIndex));
  };

  if (!shiftPatterns.length) {
    return (
      <>
        {/* Worker Attendance Scanner hidden for now */}
        {/* <BookingAttendanceScanner booking={booking} /> */}
        <Card className="mt-3">
          <CardBody className="text-muted text-center py-4">
            No shift patterns available for attendance.
          </CardBody>
        </Card>
      </>
    );
  }

  return (
    <>
      {/* Worker Attendance Scanner hidden for now */}
      {/* <BookingAttendanceScanner booking={booking} /> */}

      <Card className="mt-3 border-0 shadow-sm">
        <CardHeader className="bg-light">
          <h5 className="mb-0">Shift attendance</h5>
        </CardHeader>
        <CardBody className="p-0">
          {shiftPatterns.map((shift, arrayIndex) => {
            const shiftKey = getShiftKey(shift, arrayIndex);
            const dates = getShiftDates(shift);
            const isOpen = openShiftIndex === arrayIndex;

            return (
              <div
                key={shiftKey}
                className={arrayIndex > 0 ? "border-top" : ""}
              >
                <CardHeader
                  className="bg-white d-flex justify-content-between align-items-center py-3"
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleShift(arrayIndex)}
                >
                  <div>
                    <div className="fw-semibold">
                      Shift {arrayIndex + 1} —{" "}
                      {shift.role?.title || "Role not specified"}
                    </div>
                    <small className="text-muted">
                      {shift.startTime} – {shift.endTime}
                      {shift.sector?.title ? ` · ${shift.sector.title}` : ""}
                    </small>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {dates.length > 0 && (
                      <Badge color="light" className="text-dark">
                        {dates.length} date{dates.length !== 1 ? "s" : ""}
                      </Badge>
                    )}
                    <i
                      className={`mdi ${
                        isOpen ? "mdi-chevron-up" : "mdi-chevron-down"
                      } text-muted`}
                    />
                  </div>
                </CardHeader>

                <Collapse isOpen={isOpen}>
                  <ShiftAttendancePanel
                    bookingSlug={bookingSlug}
                    bookingTimezone={booking?.timezone}
                    shift={shift}
                    shiftKey={shiftKey}
                    isOpen={isOpen}
                    activeDateByShift={activeDateByShift}
                    setActiveDateByShift={setActiveDateByShift}
                    statusFilterByKey={statusFilterByKey}
                    setStatusFilterByKey={setStatusFilterByKey}
                    onOpenProfile={openProfileModal}
                  />
                </Collapse>
              </div>
            );
          })}
        </CardBody>
      </Card>

      <Modal
        isOpen={isProfileOpen}
        toggle={() => setIsProfileOpen(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setIsProfileOpen(false)}>
          Worker profile
        </ModalHeader>
        <ModalBody>
          {profileUser ? <WorkerMiniProfile user={profileUser} /> : null}
        </ModalBody>
      </Modal>
    </>
  );
}

BookingAttendanceTab.propTypes = {
  booking: PropTypes.object.isRequired,
};
